import './index.less';

import {
  Button,
  Modal,
  Radio,
  Space,
  Table,
  Descriptions,
  Input,
  Select,
  InputNumber,
  message,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { cloneDeep } from 'lodash-es';
import { RevxButton } from 'components/RevxButton';
import {
  SkuDynamicTemplateCategoryDTO,
  SkuDynamicTemplateDTO,
  SkuDynamicTemplateType,
  SkuDynamicTemplateMappingSimpleDTO,
} from 'domains/skuDomain';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import { RevxSpin } from 'components/RevxSpin';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'utils/pdfWorker';

interface RevxDynamicTemplateModalProps {
  visible: boolean;
  skuId?: number;
  onCancel: () => void;
  onConfirm: (
    frontTemplate?: SkuDynamicTemplateDTO,
    backTemplate?: SkuDynamicTemplateDTO
  ) => void;
}

enum StepEnum {
  SELECT_CATEGORY = 1,
  SELECT_TEMPLATE = 2,
}

export const RevxDynamicTemplateModal: React.FC<RevxDynamicTemplateModalProps> =
  ({ visible, skuId, onCancel, onConfirm }) => {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState<StepEnum>(
      StepEnum.SELECT_CATEGORY
    );
    const [loading, setLoading] = useState(false);

    // Category 相关状态
    const [categories, setCategories] = useState<
      SkuDynamicTemplateCategoryDTO[]
    >([]);
    const [selectedCategoryId, setSelectedCategoryId] =
      useState<number | undefined>();

    // Template 相关状态
    const [templates, setTemplates] = useState<SkuDynamicTemplateDTO[]>([]);
    const [selectedFrontTemplateId, setSelectedFrontTemplateId] =
      useState<number | undefined>();
    const [selectedBackTemplateId, setSelectedBackTemplateId] =
      useState<number | undefined>();
    const [viewingTemplate, setViewingTemplate] =
      useState<SkuDynamicTemplateDTO | undefined>();

    const [savedMapping, setSavedMapping] =
      useState<SkuDynamicTemplateMappingSimpleDTO | undefined>();

    // 当模态框显示时，重置状态并加载 categories
    useEffect(() => {
      const init = async () => {
        resetState();
        try {
          setLoading(true);
          const cats = await skuService.getSkuDynamicTemplateCategory();
          setCategories(cats);

          if (skuId) {
            const mapping = await skuService.getSkuSimpleDynamicMapping(skuId);
            if (mapping) {
              setSavedMapping(mapping);
              if (
                mapping.categoryId &&
                cats.some((c) => c.id === mapping.categoryId)
              ) {
                setSelectedCategoryId(mapping.categoryId);

                // Load templates logic inline to avoid state update delays
                const tpls = await skuService.getSkuDynamicTemplateByCategoryId(
                  mapping.categoryId
                );
                setTemplates(tpls);
                setCurrentStep(StepEnum.SELECT_TEMPLATE);

                if (mapping.frontTemplateId) {
                  setSelectedFrontTemplateId(mapping.frontTemplateId);
                }
                if (mapping.backendTemplateId) {
                  setSelectedBackTemplateId(mapping.backendTemplateId);
                }
              }
            }
          }
        } catch (error) {
          console.error('Failed to init dynamic template modal:', error);
        } finally {
          setLoading(false);
        }
      };

      if (visible) {
        init();
      }
    }, [visible, skuId]);

    const resetState = () => {
      setCurrentStep(StepEnum.SELECT_CATEGORY);
      setSelectedCategoryId(undefined);
      setSelectedFrontTemplateId(undefined);
      setSelectedBackTemplateId(undefined);
      setTemplates([]);
      setSavedMapping(undefined);
    };

    const loadTemplates = async (categoryId: number) => {
      try {
        setLoading(true);
        const result = await skuService.getSkuDynamicTemplateByCategoryId(
          categoryId
        );
        setTemplates(result);
      } catch (error) {
        console.error('Failed to load templates:', error);
      } finally {
        setLoading(false);
      }
    };

    const handleCategoryNext = async () => {
      if (!selectedCategoryId) return;

      await loadTemplates(selectedCategoryId);

      if (savedMapping && savedMapping.categoryId === selectedCategoryId) {
        if (savedMapping.frontTemplateId) {
          setSelectedFrontTemplateId(savedMapping.frontTemplateId);
        }
        if (savedMapping.backendTemplateId) {
          setSelectedBackTemplateId(savedMapping.backendTemplateId);
        }
      }

      setCurrentStep(StepEnum.SELECT_TEMPLATE);
    };

    const handleBack = () => {
      setCurrentStep(StepEnum.SELECT_CATEGORY);
      setSelectedFrontTemplateId(undefined);
      setSelectedBackTemplateId(undefined);
    };

    const handleConfirm = async () => {
      const frontTemplate = templates.find(
        (t) => t.id === selectedFrontTemplateId
      );
      const backTemplate = templates.find(
        (t) => t.id === selectedBackTemplateId
      );

      if (skuId && selectedFrontTemplateId) {
        try {
          setLoading(true);
          await skuService.saveSkuSimpleDynamicMapping({
            skuId,
            categoryId: selectedCategoryId,
            frontTemplateId: selectedFrontTemplateId,
            backendTemplateId: selectedBackTemplateId,
          });
        } catch (error) {
          console.error('Failed to save mapping:', error);
        } finally {
          setLoading(false);
        }
      }

      onConfirm(frontTemplate, backTemplate);
      resetState();
    };

    const handleCancel = () => {
      onCancel();
      resetState();
    };

    // 按照正面和反面分类 templates - 根据 templateType 字段
    const frontTemplates = templates.filter(
      (t) => t.templateType === SkuDynamicTemplateType.FRONT
    );

    const backTemplates = templates.filter(
      (t) => t.templateType === SkuDynamicTemplateType.BACKEND
    );

    const renderTemplateDetailsModal = () => {
      const updateTemplateField = (record: any, field: string, value: any) => {
        if (!viewingTemplate) return;
        const newTemplate = cloneDeep(viewingTemplate);
        const { _originalIndex, _childIndex } = record;

        if (
          newTemplate.customerFields &&
          typeof _originalIndex === 'number' &&
          newTemplate.customerFields[_originalIndex]
        ) {
          if (typeof _childIndex === 'number') {
            const parent = newTemplate.customerFields[_originalIndex];
            if (parent.multiFieldInfos && parent.multiFieldInfos[_childIndex]) {
              const targetField = field === 'title' ? 'name' : field;
              // @ts-ignore
              parent.multiFieldInfos[_childIndex][targetField] = value;
            }
          } else {
            // @ts-ignore
            newTemplate.customerFields[_originalIndex][field] = value;
          }
          setViewingTemplate(newTemplate);
        }
      };

      const handleSaveTemplate = async () => {
        if (!viewingTemplate) return;
        try {
          await skuService.saveSkuDynamicTemplate(viewingTemplate);
          message.success('Template saved successfully');
          setTemplates((prev) =>
            prev.map((t) => (t.id === viewingTemplate.id ? viewingTemplate : t))
          );
          setViewingTemplate(undefined);
        } catch (error) {
          console.error(error);
          message.error('Failed to save template');
        }
      };

      const dataSource =
        viewingTemplate?.customerFields
          ?.map((f, i) => ({ ...f, _originalIndex: i }))
          .sort((a, b) => (a.index || 0) - (b.index || 0))
          .map((f) => ({
            ...f,
            key: f.name || f.title || `field_${f._originalIndex}`,
            children:
              f.multiFieldInfos && f.multiFieldInfos.length > 0
                ? f.multiFieldInfos.map((mf, idx) => ({
                    ...mf,
                    key: `${f.name || f.title}_child_${idx}`,
                    _originalIndex: f._originalIndex,
                    _childIndex: idx,
                    index: undefined,
                    title: mf.name || 'Multi Field',
                    type: 'MULTI_FIELD',
                  }))
                : undefined,
          })) || [];

      return (
        <Modal
          visible={!!viewingTemplate}
          title="Template Details"
          onCancel={() => setViewingTemplate(undefined)}
          footer={[
            <Button key="close" onClick={() => setViewingTemplate(undefined)}>
              Close
            </Button>,
            <Button key="save" type="primary" onClick={handleSaveTemplate}>
              Save
            </Button>,
          ]}
          width={1500}
          destroyOnClose
        >
          {viewingTemplate && (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {viewingTemplate.originalTemplateUrl && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    padding: '20px',
                    borderRadius: '4px',
                  }}
                >
                  <Document
                    file={viewingTemplate.originalTemplateUrl}
                    loading={<div className="pdf-loading">Loading...</div>}
                    error={<div className="pdf-error">Failed to load PDF</div>}
                  >
                    <Page
                      pageNumber={1}
                      scale={1.5}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
              )}

              <Descriptions bordered column={2}>
                <Descriptions.Item label="Template Name" span={2}>
                  {viewingTemplate.templateName}
                </Descriptions.Item>
                <Descriptions.Item label="Template Type">
                  {viewingTemplate.templateType}
                </Descriptions.Item>
              </Descriptions>

              <div>
                <h4>Customer Fields</h4>
                <Table
                  dataSource={dataSource}
                  rowKey="key"
                  pagination={false}
                  size="small"
                  scroll={{ y: 600 }}
                  columns={[
                    {
                      title: 'Index',
                      dataIndex: 'index',
                      key: 'index',
                      width: 80,
                    },
                    {
                      title: 'Name',
                      dataIndex: 'name',
                      key: 'name',
                      width: 150,
                      ellipsis: true,
                    },
                    {
                      title: 'Title',
                      dataIndex: 'title',
                      key: 'title',
                      width: 200,
                      render: (text, record) => (
                        <Input
                          value={text}
                          onChange={(e) =>
                            updateTemplateField(record, 'title', e.target.value)
                          }
                        />
                      ),
                    },
                    {
                      title: 'Type',
                      dataIndex: 'type',
                      key: 'type',
                      width: 120,
                    },
                    {
                      title: 'Font',
                      dataIndex: 'font',
                      key: 'font',
                      width: 180,
                      render: (text, record) => (
                        <Select
                          value={text}
                          onChange={(val) =>
                            updateTemplateField(record, 'font', val)
                          }
                          style={{ width: '100%' }}
                          allowClear
                        >
                          <Select.Option value="Frutiger-BoldCn">
                            Frutiger-BoldCn
                          </Select.Option>
                          <Select.Option value="Frutiger-Cn">
                            Frutiger-Cn
                          </Select.Option>
                        </Select>
                      ),
                    },
                    {
                      title: 'Font Size',
                      dataIndex: 'fontSize',
                      key: 'fontSize',
                      width: 100,
                      render: (val, record) => (
                        <InputNumber
                          value={val}
                          onChange={(v) =>
                            updateTemplateField(record, 'fontSize', v)
                          }
                        />
                      ),
                    },
                    {
                      title: 'X',
                      dataIndex: 'x',
                      key: 'x',
                      width: 100,
                      render: (val, record) => (
                        <InputNumber
                          value={val}
                          onChange={(v) => updateTemplateField(record, 'x', v)}
                        />
                      ),
                    },
                    {
                      title: 'Y',
                      dataIndex: 'y',
                      key: 'y',
                      width: 100,
                      render: (val, record) => (
                        <InputNumber
                          value={val}
                          onChange={(v) => updateTemplateField(record, 'y', v)}
                        />
                      ),
                    },
                    {
                      title: 'Max Length',
                      dataIndex: 'maxLength',
                      key: 'maxLength',
                      width: 120,
                      render: (val, record) => (
                        <InputNumber
                          value={val}
                          onChange={(v) =>
                            updateTemplateField(record, 'maxLength', v)
                          }
                        />
                      ),
                    },
                    {
                      title: 'Over Length Strategy',
                      dataIndex: 'overLengthStrategy',
                      key: 'overLengthStrategy',
                      width: 220,
                      render: (text, record) => (
                        <Select
                          value={text}
                          onChange={(val) =>
                            updateTemplateField(
                              record,
                              'overLengthStrategy',
                              val
                            )
                          }
                          style={{ width: '100%' }}
                          allowClear
                        >
                          {[
                            'IGNORE',
                            'REDUCE_FONT',
                            'REDUCE_FONT_ERROR',
                            'REDUCE_FONT_NEW_LINE',
                            'NEW_LINE',
                          ].map((opt) => (
                            <Select.Option key={opt} value={opt}>
                              {opt}
                            </Select.Option>
                          ))}
                        </Select>
                      ),
                    },
                  ]}
                />
              </div>
            </Space>
          )}
        </Modal>
      );
    };

    const renderCategoryStep = () => (
      <div className="dynamic-template-modal-content">
        <p className="dynamic-template-modal-description">
          Please select a template category to continue
        </p>
        <Radio.Group
          value={selectedCategoryId}
          onChange={(e) => setSelectedCategoryId(e.target.value)}
          className="dynamic-template-radio-group"
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {categories.map((category) => (
              <Radio
                key={category.id}
                value={category.id}
                className="dynamic-template-radio"
              >
                <div className="dynamic-template-option">
                  <div className="dynamic-template-title">
                    {category.categoryName}
                  </div>
                  {category.galleryLabelName && (
                    <div className="dynamic-template-desc">
                      Gallery Label: {category.galleryLabelName}
                    </div>
                  )}
                </div>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </div>
    );

    const renderTemplateStep = () => (
      <div className="dynamic-template-modal-content">
        <p className="dynamic-template-modal-description">
          Please select templates for configuration
        </p>

        <div className="dynamic-template-section">
          <div className="dynamic-template-section-title">Front Template</div>
          <Radio.Group
            value={selectedFrontTemplateId}
            onChange={(e) => setSelectedFrontTemplateId(e.target.value)}
            className="dynamic-template-radio-group"
          >
            <div className="dynamic-template-grid">
              {frontTemplates.map((template) => (
                <Radio
                  key={template.id}
                  value={template.id}
                  className="dynamic-template-radio-card"
                >
                  {template.originalTemplateUrl && (
                    <div className="dynamic-template-preview">
                      {' '}
                      <Button
                        type="text"
                        icon={<EyeOutlined />}
                        className="dynamic-template-detail-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setViewingTemplate(template);
                        }}
                      />{' '}
                      <Document
                        file={template.originalTemplateUrl}
                        loading={<div className="pdf-loading">Loading...</div>}
                        error={
                          <div className="pdf-error">Failed to load PDF</div>
                        }
                      >
                        <Page
                          pageNumber={1}
                          scale={4.0}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                    </div>
                  )}
                  <div className="dynamic-template-title">
                    {template.templateName}
                  </div>
                </Radio>
              ))}
            </div>
          </Radio.Group>
        </div>

        {backTemplates.length > 0 && (
          <div className="dynamic-template-section">
            <div className="dynamic-template-section-title">
              Back Template (optional)
            </div>
            <Radio.Group
              value={selectedBackTemplateId}
              onChange={(e) => setSelectedBackTemplateId(e.target.value)}
              className="dynamic-template-radio-group"
            >
              <div className="dynamic-template-grid">
                {backTemplates.map((template) => (
                  <Radio
                    key={template.id}
                    value={template.id}
                    className="dynamic-template-radio-card"
                  >
                    {template.originalTemplateUrl && (
                      <div className="dynamic-template-preview">
                        <Button
                          type="text"
                          icon={<EyeOutlined />}
                          className="dynamic-template-detail-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setViewingTemplate(template);
                          }}
                        />
                        <Document
                          file={template.originalTemplateUrl}
                          loading={
                            <div className="pdf-loading">Loading...</div>
                          }
                          error={
                            <div className="pdf-error">Failed to load PDF</div>
                          }
                        >
                          <Page
                            pageNumber={1}
                            scale={4.0}
                            renderTextLayer={false}
                            renderAnnotationLayer={false}
                          />
                        </Document>
                      </div>
                    )}
                    <div className="dynamic-template-title">
                      {template.templateName}
                    </div>
                  </Radio>
                ))}
              </div>
            </Radio.Group>
          </div>
        )}
      </div>
    );

    const getModalTitle = () => {
      if (currentStep === StepEnum.SELECT_CATEGORY) {
        return 'Select Template Category';
      }
      return 'Select Template';
    };

    const getFooterButtons = () => {
      if (currentStep === StepEnum.SELECT_CATEGORY) {
        return [
          <RevxButton key="cancel" onClick={handleCancel}>
            {t('common.cancel')}
          </RevxButton>,
          <RevxButton
            key="next"
            type="primary"
            brandGreen
            onClick={handleCategoryNext}
            disabled={!selectedCategoryId}
          >
            {t('common.next')}
          </RevxButton>,
        ];
      }

      return [
        <RevxButton key="back" onClick={handleBack}>
          {t('common.back')}
        </RevxButton>,
        <RevxButton key="cancel" onClick={handleCancel}>
          {t('common.cancel')}
        </RevxButton>,
        <RevxButton
          key="confirm"
          type="primary"
          brandGreen
          onClick={handleConfirm}
          disabled={!selectedFrontTemplateId}
        >
          {t('common.confirm')}
        </RevxButton>,
      ];
    };

    return (
      <Modal
        title={getModalTitle()}
        visible={visible}
        onCancel={handleCancel}
        footer={getFooterButtons()}
        width={1500}
        className="dynamic-template-modal"
        destroyOnClose
      >
        <RevxSpin spinning={loading}>
          {currentStep === StepEnum.SELECT_CATEGORY && renderCategoryStep()}
          {currentStep === StepEnum.SELECT_TEMPLATE && renderTemplateStep()}
        </RevxSpin>
        {renderTemplateDetailsModal()}
      </Modal>
    );
  };
